// DEV-NOTE: These are queries which is NOT a part of redux-thunk service pattern
// Notice: using await/async will not simplify. You want to use loading indicator. You do not want to await code.
// Naming Convention: Api-calls are post-fixed Async to make it clear that data is not axcessable on execution.

import { useState } from 'react';

import { AxiosError } from 'axios';
import { toast } from 'react-toastify';

import { SpreadPerformanceRequest } from '../types/dto-entities';
import {
  AtrRequest,
  AtrResponse,
  BetaRequest,
  CloseOptionRequest,
  GreeksParams,
  HistoricalDataParams,
  LossRangeRequest,
  MarketDataParams,
  OptionChainParams,
  OptionContract,
  OptionGreeks,
  OrderState,
  PlaceOrderParams,
  PlaceOrderReport,
  RollOptionRequest,
  JobScheduleInfo,
  SpreadPoint
} from '../types/entities';
import { BarSize, MarketPriceType, OrderStatusType, ManualTaskAction, WhatToShow } from '../types/enums';
import { Leg } from '../types/strategy-entities';
import { ContractDetails } from '../types/tws-entities';
import { deepCopyContractDetails } from '../utils/contract-details-utils';

import {
  checkBrokerConnection,
  checkOrderMargin,
  fetchOptionChain,
  getHistoricalVolatility,
  getImpliedVolatility,
  isScheduleRunning,
  placeOrder,
  runStrategyTask,
  toggleScheduleRunning,
  fetchMarketPrice,
  getOptionGreeks,
  getAtmOptionLookup,
  rollOptionContract,
  closeOptionContract,
  getAverageTrueRange,
  getLossRange,
  calculateBeta,
  getSpreadPerformance
} from './api';

const showToastError = (e: AxiosError) => {
  const error = e as AxiosError<{ message: string }>;
  const msg = error.response?.data.message || error.message;
  toast.error(msg);
};

export const useBrokerConnection = (): {
  checkBrokerConnectionAsync: () => void;
  loading: boolean;
  connected: boolean;
  error: string | undefined;
} => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const checkBrokerConnectionAsync = () => {
    setLoading(true);
    checkBrokerConnection()
      .then((response) => {
        if (!!response.data && response.data === 'success') {
          setConnected(true);
          setError(undefined);
        } else {
          setConnected(false);
          const hasCustomErrorMsg = response?.data && response.data !== 'success' && response.data !== 'failed';
          if (hasCustomErrorMsg) {
            setError(response?.data);
          }
        }
      })
      .catch((e) => showToastError(e))
      .finally(() => setLoading(false));
  };
  return { checkBrokerConnectionAsync, loading, connected, error };
};

export const useMarketPrice = (): {
  getMarketPriceAsync: (type: MarketPriceType, details: ContractDetails, underlying?: boolean) => void;
  loading: boolean;
  marketPrice: number;
  isUnderlying: boolean;
} => {
  const [loading, setLoading] = useState(false);
  const [marketPrice, setMarketPrice] = useState<number>(0);
  const [isUnderlying, setIsUnderlying] = useState(false);

  const getMarketPriceAsync = (type: MarketPriceType, details: ContractDetails, underlying = false) => {
    setIsUnderlying(underlying);
    setLoading(true);

    const conId = underlying ? details.underConId : details.contract.conId;
    const symbol = underlying ? details.underSymbol : details.contract.symbol;
    const secType = underlying ? details.underSecType : details.contract.secType;
    const params: MarketDataParams = {
      conId,
      symbol,
      localSymbol: '',
      currency: details.contract.currency,
      exchange: details.contract.exchange,
      secType,
      right: details.contract.right,
      strike: details.contract.strike,
      lastTradeDateOrContractMonth: details.contract.lastTradeDateOrContractMonth,
      multiplier: details.contract.multiplier
    };

    fetchMarketPrice(type, params)
      .then((response) => setMarketPrice(response.data))
      .catch((e: AxiosError) => showToastError(e))
      .finally(() => setLoading(false));
  };
  return { getMarketPriceAsync, loading, marketPrice, isUnderlying };
};

export const useAverageTrueRange = (): {
  getAverageTrueRangeAsync: (details: ContractDetails, barSize: BarSize, periods: number, orderSize: number) => void;
  loading: boolean;
  atrResponse: AtrResponse;
} => {
  const [loading, setLoading] = useState(false);
  const [atrResponse, setAtrResponse] = useState<AtrResponse>({ atrValue: 0, atrValuePnl: 0 });

  const getAverageTrueRangeAsync = (details: ContractDetails, barSize: BarSize, periods: number, orderSize: number) => {
    const copy = deepCopyContractDetails(details);
    const request: AtrRequest = {
      details: copy,
      barSize,
      periods,
      orderSize
    };

    setLoading(true);
    getAverageTrueRange(request)
      .then((response) => setAtrResponse(response.data))
      .catch((e: AxiosError) => showToastError(e))
      .finally(() => setLoading(false));
  };
  return { getAverageTrueRangeAsync, atrResponse, loading };
};

export const useBetaValue = (): {
  calculateBetaAsync: (details: ContractDetails, betaDetails: ContractDetails, barSize: BarSize, periods: number) => void;
  loading: boolean;
  beta: number;
} => {
  const [loading, setLoading] = useState(false);
  const [beta, setBeta] = useState(0);

  const calculateBetaAsync = (details: ContractDetails, betaDetails: ContractDetails, barSize: BarSize, periods: number) => {
    const copy = deepCopyContractDetails(details);
    const betaCopy = deepCopyContractDetails(betaDetails);
    const request: BetaRequest = {
      details: copy,
      betaDetails: betaCopy,
      barSize,
      periods
    };

    setLoading(true);
    calculateBeta(request)
      .then((response) => setBeta(response.data))
      .catch((e: AxiosError) => showToastError(e))
      .finally(() => setLoading(false));
  };
  return { calculateBetaAsync, beta, loading };
};

export const useLossRange = (): {
  getLossRangeAsync: (details: ContractDetails, loss: number, orderSize: number) => void;
  loading: boolean;
  lossRange: number;
} => {
  const [loading, setLoading] = useState(false);
  const [lossRange, setLossRange] = useState<number>(0);

  const getLossRangeAsync = (details: ContractDetails, loss: number, orderSize: number) => {
    const copy = deepCopyContractDetails(details);
    const request: LossRangeRequest = {
      details: copy,
      loss,
      orderSize
    };

    setLoading(true);
    getLossRange(request)
      .then((response) => setLossRange(response.data))
      .catch((e: AxiosError) => showToastError(e))
      .finally(() => setLoading(false));
  };
  return { getLossRangeAsync, lossRange, loading };
};

export const useSpreadPerformance = (): {
  getSpreadPerformanceAsync: (request: SpreadPerformanceRequest) => void;
  loading: boolean;
  response: SpreadPoint[];
} => {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<SpreadPoint[]>([]);

  const getSpreadPerformanceAsync = (request: SpreadPerformanceRequest) => {
    setLoading(true);
    getSpreadPerformance(request)
      .then((response) => setResponse(response.data))
      .catch((e: AxiosError) => showToastError(e))
      .finally(() => setLoading(false));
  };
  return { getSpreadPerformanceAsync, response, loading };
};

export const useImpliedVolatilty = (): {
  getImpliedVolatilityAsync: (details: ContractDetails, underlying?: boolean) => void;
  loading: boolean;
  impliedVolatility: number;
} => {
  const [loading, setLoading] = useState(false);
  const [impliedVolatility, setImpliedVolatility] = useState<number>(0);

  const getImpliedVolatilityAsync = (details: ContractDetails, underlying = false) => {
    const conId = underlying ? details.underConId : details.contract.conId;
    const symbol = underlying ? details.underSymbol : details.contract.symbol;
    const secType = underlying ? details.underSecType : details.contract.secType;
    const params: MarketDataParams = {
      conId,
      symbol,
      localSymbol: '',
      currency: details.contract.currency,
      exchange: details.contract.exchange,
      secType,
      right: undefined,
      strike: 0,
      lastTradeDateOrContractMonth: undefined,
      multiplier: undefined
    };

    setLoading(true);
    getImpliedVolatility(params)
      .then((response) => setImpliedVolatility(response.data))
      .catch((e: AxiosError) => showToastError(e))
      .finally(() => setLoading(false));
  };
  return { getImpliedVolatilityAsync, loading, impliedVolatility };
};

export const useHistoricalVolatilty = (): {
  getHistoricalVolatilityAsync: (details: ContractDetails) => void;
  loading: boolean;
  historicalVolatility: number;
} => {
  const [loading, setLoading] = useState(false);
  const [historicalVolatility, setHistoricalVolatility] = useState<number>(0);

  const getHistoricalVolatilityAsync = (details: ContractDetails) => {
    const useRth = details.contract.secType === 'STK' || details.contract.secType === 'OPT';
    const isOption = details.contract.secType === 'FOP' || details.contract.secType === 'OPT';
    const conId = isOption ? details.underConId : details.contract.conId;
    const symbol = isOption ? details.underSymbol : details.contract.symbol;
    const secType = isOption ? details.underSecType : details.contract.secType;
    const params: HistoricalDataParams = {
      conId,
      symbol,
      exchange: details.contract.exchange,
      secType,
      barSize: BarSize._1_DAY,
      durationInDays: 40,
      whatToShow: WhatToShow.TRADES,
      useRth
    };

    setLoading(true);
    getHistoricalVolatility(params)
      .then((response) => setHistoricalVolatility(response.data))
      .catch((e: AxiosError) => showToastError(e))
      .finally(() => setLoading(false));
  };
  return { getHistoricalVolatilityAsync, loading, historicalVolatility };
};

export const usePlaceOrder = (): {
  placeOrderAsync: (args: PlaceOrderParams) => void;
  loading: boolean;
  placeOrderReport: PlaceOrderReport | undefined;
} => {
  const [loading, setLoading] = useState(false);
  const [placeOrderReport, setPlaceOrderReport] = useState<PlaceOrderReport | undefined>();

  const placeOrderAsync = (args: PlaceOrderParams) => {
    setLoading(true);
    placeOrder(args)
      .then((response) => {
        const report = response.data;
        if (report.status !== OrderStatusType.Cancelled && report.status !== OrderStatusType.ApiCancelled) {
          toast.success(`Order submitted with status: ${OrderStatusType[report.status]}`);
        } else {
          toast.warning(`Order submitted with status: ${OrderStatusType[report.status]}`);
        }
        setPlaceOrderReport(response.data);
      })
      .catch((error) => showToastError(error))
      .finally(() => setLoading(false));
  };

  return {
    placeOrderAsync,
    loading,
    placeOrderReport
  };
};

export const useRollOptionContract = (): {
  rollOptionContractAsync: (args: RollOptionRequest) => void;
  loading: boolean;
  placeOrderReport: PlaceOrderReport | undefined;
} => {
  const [loading, setLoading] = useState(false);
  const [placeOrderReport, setPlaceOrderReport] = useState<PlaceOrderReport | undefined>();

  const rollOptionContractAsync = (args: RollOptionRequest) => {
    setLoading(true);
    rollOptionContract(args)
      .then((response) => {
        const report = response.data;
        if (report.status !== OrderStatusType.Cancelled && report.status !== OrderStatusType.ApiCancelled) {
          toast.success(`Order submitted with status: ${OrderStatusType[report.status]}`);
        } else {
          toast.warning(`Order submitted with status: ${OrderStatusType[report.status]}`);
        }
        setPlaceOrderReport(response.data);
      })
      .catch((error) => showToastError(error))
      .finally(() => setLoading(false));
  };

  return {
    rollOptionContractAsync,
    loading,
    placeOrderReport
  };
};

export const useCloseOptionContract = (): {
  closeOptionContractAsync: (args: CloseOptionRequest) => void;
  loading: boolean;
  placeOrderReport: PlaceOrderReport | undefined;
} => {
  const [loading, setLoading] = useState(false);
  const [placeOrderReport, setPlaceOrderReport] = useState<PlaceOrderReport | undefined>();

  const closeOptionContractAsync = (args: CloseOptionRequest) => {
    setLoading(true);
    closeOptionContract(args)
      .then((response) => {
        const report = response.data;
        if (report.status !== OrderStatusType.Cancelled && report.status !== OrderStatusType.ApiCancelled) {
          toast.success(`Order submitted with status: ${OrderStatusType[report.status]}`);
        } else {
          toast.warning(`Order submitted with status: ${OrderStatusType[report.status]}`);
        }
        setPlaceOrderReport(response.data);
      })
      .catch((error) => showToastError(error))
      .finally(() => setLoading(false));
  };

  return {
    closeOptionContractAsync,
    loading,
    placeOrderReport
  };
};

export const useCheckMargin = () => {
  const [loading, setLoading] = useState(false);
  const [orderState, setOrderState] = useState<OrderState | undefined>();

  const checkMarginAsync = (args: PlaceOrderParams) => {
    setLoading(true);
    checkOrderMargin(args)
      .then((response) => {
        setOrderState(response.data);
      })
      .catch((error) => showToastError(error))
      .finally(() => setLoading(false));
  };

  return {
    checkMarginAsync,
    loading,
    orderState
  };
};

export const useOptionChain = (): {
  fetchOptionChainAsync: (args: OptionChainParams) => void;
  loading: boolean;
  error: string | undefined;
  options: OptionContract[];
} => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [options, setOptions] = useState<OptionContract[]>([]);

  const fetchOptionChainAsync = (args: OptionChainParams) => {
    setLoading(true);
    setError(undefined);
    fetchOptionChain(args)
      .then((response) => setOptions(response.data))
      .catch((error) => {
        const msg = error.response?.data.message || error.message;
        setError(msg);
      })
      .finally(() => setLoading(false));
  };

  return {
    fetchOptionChainAsync,
    loading,
    error,
    options
  };
};

export const useServerSchedule = (): {
  checkScheduleInfoAsync: () => void;
  toggleScheduleRunAsync: () => void;
  loading: boolean;
  error: string | undefined;
  scheduleInfo: JobScheduleInfo;
} => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [scheduleInfo, setScheduleInfo] = useState<JobScheduleInfo>({ jobName: '', repeats: 0, state: '' });

  const checkScheduleInfoAsync = () => {
    setLoading(true);
    setError(undefined);
    isScheduleRunning()
      .then((response) => {
        const schedules = response.data;
        setScheduleInfo(schedules[0]);
      })
      .catch((error) => {
        const msg = error.response?.data.message || error.message;
        setError(msg);
      })
      .finally(() => setLoading(false));
  };

  const toggleScheduleRunAsync = () => {
    setLoading(true);
    toggleScheduleRunning()
      .then((response) => {
        const newState = response.data;
        const copy = {
          ...scheduleInfo,
          state: newState
        };
        setScheduleInfo(copy);
      })
      .catch((error) => {
        showToastError(error);
      })
      .finally(() => setLoading(false));
  };

  return {
    checkScheduleInfoAsync,
    toggleScheduleRunAsync,
    loading,
    error,
    scheduleInfo
  };
};

export const useRunStrategyTask = (): {
  runStrategyAsync: (strategyId: string, manualTaskAction: ManualTaskAction) => void;
  loading: boolean;
} => {
  const [loading, setLoading] = useState(false);
  const runStrategyAsync = (strategyId: string, manualTaskAction: ManualTaskAction) => {
    setLoading(true);
    runStrategyTask(strategyId, { strategyId: strategyId, manualTaskAction })
      .then((response) => {
        if (response.data) {
          toast.warn(`Strategy executed with errors: ${response.data}`);
        } else {
          toast.success('Strategy executed successfully!');
        }
      })
      .catch((error) => showToastError(error))
      .finally(() => setLoading(false));
  };

  return {
    runStrategyAsync,
    loading
  };
};

export const useOptionGreeks = (): {
  getOptionGreeksAsync: (params: GreeksParams, callback: (greeks: OptionGreeks) => void) => void;
  loading: boolean;
} => {
  const [loading, setLoading] = useState(false);
  const getOptionGreeksAsync = (params: GreeksParams, callback: (greeks: OptionGreeks) => void) => {
    setLoading(true);
    getOptionGreeks(params)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => showToastError(error))
      .finally(() => setLoading(false));
  };

  return {
    getOptionGreeksAsync,
    loading
  };
};

export const useAtmOptionLookup = (): {
  atmOptionLookupsAsync: (leg: Leg, callback: (opt: ContractDetails) => void) => void;
  loading: boolean;
} => {
  const [loading, setLoading] = useState(false);
  const atmOptionLookupsAsync = (leg: Leg, callback: (opt: ContractDetails) => void) => {
    setLoading(true);
    getAtmOptionLookup(leg)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => showToastError(error))
      .finally(() => setLoading(false));
  };

  return {
    atmOptionLookupsAsync,
    loading
  };
};
